import React from 'react';
import * as yup from 'yup';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import Typography from '@mui/material/Typography';
import { yupResolver } from '@hookform/resolvers/yup';
import Button from '@mui/material/Button';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';

import { logger } from '../../logging';
import {
  ExcelUploadField,
  SubmissionButton,
} from '../../components';
import { jarvisApi } from '../../api';
import { useAuth } from '../../auth';
import { CONTAINERS } from '../../../../shared-jarvis';
import {
  EnterpriseSearchField,
  FileNameField,
  NotifyEmailListField,
} from '../fields';
import { parcelFieldSchema } from '../parcelFieldSchema';
import { parcelRequestColumnBuilders } from './parcelWorkbookColumnBuilders';
import {
  withUploadToBlobStorage,
  WithUploadToBlobStorageProps,
} from '../withUploadToBlobStorage';

const schema = yup.object({
  ...parcelFieldSchema,
});

export type ParcelRateFormFields = yup.InferType<typeof schema>;

const Form = ({
  handleFormSubmit,
  submitting,
}: WithUploadToBlobStorageProps<ParcelRateFormFields>) => {
  const auth = useAuth();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ParcelRateFormFields>({
    resolver: yupResolver(schema),
    defaultValues: {
      returnCustomerRates: false,
      rateUps: true,
      rateFedEx: true,
      accounts: [],
      notifyEmails: auth.user?.email ? [{ value: auth.user.email }] : [],
    },
    shouldUnregister: true,
  });

  const onSubmit: SubmitHandler<ParcelRateFormFields> = async (formData) => {
    handleFormSubmit(
      formData,
      CONTAINERS.RATEQUALITY_SHIPMENTS_PARCEL_RATE,
      parcelRequestColumnBuilders
    );
  };

  return (
    <>
      <Typography
        align="center"
        component="h1"
        variant="h4"
        sx={{ mb: 3, mt: 2 }}
      >
        Parcel Rate Request
      </Typography>

      <Typography align="center">
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={async () => {
            try {
              await jarvisApi.fetchParcelRateTemplate();
              logger.info('Download Success: Parcel Rate Template');
            } catch (e: any) {
              logger.error('Download Error: Parcel Rate Template', {
                error: { message: e.message, stack: e.stack },
              });
            }
          }}
        >
          Download Template
        </Button>
      </Typography>

      <form
        style={{ marginTop: '1rem', marginBottom: 0 }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <FileNameField<ParcelRateFormFields>
          control={control}
          name="fileName"
          error={errors.fileName}
        />

        <ExcelUploadField
          label="Shipment Data"
          name="shipmentsFileUpload"
          control={control}
          error={errors.shipmentsFileUpload}
        />

        <EnterpriseSearchField<ParcelRateFormFields>
          control={control}
          error={errors.accounts as any}
          helperText="Search by Account Name or Number (select up to 4 accounts)"
          label="Rating Accounts"
          multiple={true}
          name="accounts"
          user={auth.user!}
        />

        <Controller
          name="returnCustomerRates"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              sx={{ mt: 2 }}
              control={
                <Checkbox
                  checked={field.value}
                  onChange={field.onChange}
                />
              }
              label="Return Customer Rates"
            />
          )}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Controller
            name="rateUps"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value}
                    onChange={field.onChange}
                  />
                }
                label="Rate UPS"
              />
            )}
          />

          <Controller
            name="rateFedEx"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value}
                    onChange={field.onChange}
                  />
                }
                label="Rate FedEx"
              />
            )}
          />
        </Box>

        <NotifyEmailListField<ParcelRateFormFields>
          control={control}
          error={errors.notifyEmails}
          name="notifyEmails"
          hidden={!auth.isBGEmployee}
        />

        <SubmissionButton
          loading={submitting}
          buttonProps={{
            sx: { mt: 3, p: 2 },
            fullWidth: true,
            type: 'submit',
          }}
        />
      </form>
    </>
  );
};

export const ParcelRateForm = withUploadToBlobStorage(Form);
